import React, { useMemo, useState } from 'react';
import { Alert, Button, Card, Descriptions, Divider, Drawer, Image, Input, Modal, Popconfirm, Space, Typography } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { formatCurrency } from 'utils/number';
import { getOrderPaymentMethodLabel } from '../paymentMethodLabel';
import OrderService from 'services/order';

const { Text } = Typography;
const { TextArea } = Input;
const service = new OrderService();

const getLineImage = (line) => {
  const image = line?.variant?.image
    || line?.variant?.images?.[0]
    || line?.product?.thumbnail
    || line?.product?.image
    || line?.product?.images?.[0]
    || null;

  if (!image) return null;
  if (typeof image === 'string') return image;
  return image?.url || null;
};

const OrderDetailMobile = ({ viewModel }) => {
  const [showActionSheet, setShowActionSheet] = useState(false);

  const {
    data,
    customer,
    lineItems,
    id,
    navigate,
    setIsModalPaymentProofOpen,
    setShowOrderPayment,
    setShowOrderHistoryModal,
    setShowOrderNoteModal,
    cancelOrder,
    finishOrder,
    createInvoice,
    createPackingList,
    createShippingLabel,
    openQuickStatusEditor,
    loadData,
  } = viewModel;

  const [invoiceEditorOpen, setInvoiceEditorOpen] = useState(false);
  const [invoiceDraft, setInvoiceDraft] = useState({
    company_name: '',
    tax_number: '',
    address: '',
    email: '',
    note: '',
  });

  const shippingStatusTitle = data.shipping_status?.title || data.shipping_status_id || '-';
  const internalStatusId = data.internal_status?.id || data.internal_status_id || data.internal?.status?.id;
  const internalStatusTitle = data.internal_status?.title
    || data.internal?.status?.title
    || data.store_setting?.order_internal_status_map?.[internalStatusId]?.title
    || internalStatusId
    || '-';

  const customerName = customer?.full_name || '-';
  const customerPhone = customer?.phone || '-';
  const shippingAddress = data.shipping_address?.address || '-';
  const orderNote = data.note || '-';
  const paymentMethodLabel = getOrderPaymentMethodLabel(data);

  const invoiceInfo = data?.invoice_info || data?.shipping_address?.invoice_info || {};
  const invoiceEnabled = Boolean(data?.store_setting?.invoice_enabled || Number(data?.tax_percent ?? 0) > 0 || Object.values(invoiceInfo || {}).some((value) => {
    if (typeof value === 'string') return value.trim().length > 0;
    return !!value;
  }));
  const taxPercent = Number(data?.tax_percent ?? invoiceInfo?.tax_percent ?? 0);

  const visibleInvoiceInfo = useMemo(() => ({
    company_name: invoiceInfo?.company_name || '',
    tax_number: invoiceInfo?.tax_number || '',
    address: invoiceInfo?.address || '',
    email: invoiceInfo?.email || '',
    note: invoiceInfo?.note || '',
  }), [JSON.stringify(invoiceInfo)]);

  const invoiceSummary = useMemo(() => {
    const parts = [
      invoiceInfo?.company_name,
      invoiceInfo?.tax_number && `MST ${invoiceInfo.tax_number}`,
      taxPercent > 0 ? `Thuế ${taxPercent}%` : null,
      Number(data?.tax_amount ?? 0) > 0 ? formatCurrency(Number(data?.tax_amount ?? 0)) : null,
    ].filter(Boolean);

    return parts.length > 0 ? parts.join(' • ') : 'Chưa có thông tin thuế';
  }, [invoiceInfo?.company_name, invoiceInfo?.tax_number, taxPercent, data?.tax_amount]);

  const openInvoiceEditor = () => {
    setInvoiceDraft({ ...visibleInvoiceInfo });
    setInvoiceEditorOpen(true);
  };

  const saveInvoiceInfo = async () => {
    const normalized = {
      company_name: String(invoiceDraft.company_name || '').trim(),
      tax_number: String(invoiceDraft.tax_number || '').trim(),
      address: String(invoiceDraft.address || '').trim(),
      email: String(invoiceDraft.email || '').trim(),
      note: String(invoiceDraft.note || '').trim(),
    };

    await service.update({
      id: data.id,
      invoice_info: Object.values(normalized).some((value) => value.length > 0) ? normalized : null,
    });
    setInvoiceEditorOpen(false);
    await loadData();
  };

  const totals = [
    { label: 'Tiền hàng', value: data.sub_amount },
    { label: 'Giảm giá', value: data.discount_amount },
    { label: 'Mã khuyến mãi', value: data.voucher_amount },
    { label: 'Xu', value: data.point_discount },
    { label: 'Phí ship', value: data.shipping_amount },
    ...(invoiceEnabled ? [{ label: `Thuế xuất${taxPercent > 0 ? ` (${taxPercent}%)` : ''}`, value: Number(data?.tax_amount ?? 0) }] : []),
  ];

  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <Card bodyStyle={{ padding: 14 }}>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {data.status == 'canceled' && (
            <Alert
              message="Đơn hàng này đã bị huỷ"
              description={`Lý do huỷ: ${data.cancel?.reason || ''}`}
              type="error"
              closable
            />
          )}

          {data.status == 'finalized' && (
            <Alert
              message="Đơn hàng này đã hoàn tất"
              description={`Ghi chú: ${data.finalized?.note || ''}`}
              type="success"
              closable
            />
          )}

          <Descriptions column={1} size="small" labelStyle={{ minWidth: 92 }}>
            <Descriptions.Item label="Mã đơn hàng">{data.id}</Descriptions.Item>
            <Descriptions.Item label="Khách hàng">{customerName}</Descriptions.Item>
            <Descriptions.Item label="SĐT">{customerPhone}</Descriptions.Item>
            <Descriptions.Item label="Thanh toán">{data.payment_status?.title || '-'}</Descriptions.Item>
            <Descriptions.Item label="Phương thức">{paymentMethodLabel}</Descriptions.Item>
            <Descriptions.Item label="Vận chuyển">{shippingStatusTitle}</Descriptions.Item>
            <Descriptions.Item label="Nội bộ">{internalStatusTitle}</Descriptions.Item>
            <Descriptions.Item label="Địa chỉ">{shippingAddress}</Descriptions.Item>
            <Descriptions.Item label="Ghi chú">{orderNote}</Descriptions.Item>
            {invoiceEnabled && (
              <Descriptions.Item label="Thuế">
                <Space align="center" size={8} style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Text style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{invoiceSummary}</Text>
                  <Button type="link" size="small" icon={<EditOutlined />} onClick={openInvoiceEditor}>Sửa</Button>
                </Space>
              </Descriptions.Item>
            )}
          </Descriptions>

          {data.commission > 0 && (
            <Descriptions column={1} size="small" labelStyle={{ minWidth: 92 }}>
              <Descriptions.Item label="CTV">{data.collaborator?.full_name || data.ref_code || '-'}</Descriptions.Item>
              <Descriptions.Item label="SĐT CTV">{data.collaborator?.phone || '-'}</Descriptions.Item>
              <Descriptions.Item label="Hoa hồng">{formatCurrency(data.commission)}</Descriptions.Item>
            </Descriptions>
          )}

          <Space.Compact block>
            <Button
              type="primary"
              block
              onClick={() => {
                setShowOrderPayment(true);
              }}
            >
              Thanh toán
            </Button>
            <Button block onClick={() => setShowActionSheet(true)}>
              Chức năng
            </Button>
          </Space.Compact>

          <Drawer
            title="Chức năng"
            placement="bottom"
            open={showActionSheet}
            onClose={() => setShowActionSheet(false)}
            height="80vh"
          >
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              {invoiceEnabled && (
                <Button
                  block
                  onClick={() => {
                    openInvoiceEditor();
                    setShowActionSheet(false);
                  }}
                >
                  Chỉnh sửa hoá đơn
                </Button>
              )}

              <Button
                type="primary"
                block
                onClick={() => {
                  navigate(`/order/edit/${id}`);
                  setShowActionSheet(false);
                }}
              >
                Sửa
              </Button>

              <Button
                block
                onClick={() => {
                  openQuickStatusEditor();
                  setShowActionSheet(false);
                }}
              >
                Cập nhật nhanh trạng thái
              </Button>

              <Divider style={{ margin: '8px 0' }} />

              {data.payment_proof?.image && (
                <Button
                  block
                  onClick={() => {
                    setIsModalPaymentProofOpen(true);
                    setShowActionSheet(false);
                  }}
                >
                  Thông tin thanh toán
                </Button>
              )}

              <Button
                block
                onClick={() => {
                  setShowOrderNoteModal(true);
                  setShowActionSheet(false);
                }}
              >
                Ghi chú
              </Button>

              <Button
                block
                onClick={() => {
                  setShowOrderHistoryModal(true);
                  setShowActionSheet(false);
                }}
              >
                Lịch sử
              </Button>


              <Divider style={{ margin: '8px 0' }} />
              <Button
                type="primary"
                block
                onClick={() => {
                  createInvoice();
                  setShowActionSheet(false);
                }}
              >
                In Hoá Đơn
              </Button>

              <Button
                type="primary"
                block
                onClick={() => {
                  createPackingList();
                  setShowActionSheet(false);
                }}
              >
                In Phiếu Đóng Gói
              </Button>

              <Button
                type="primary"
                block
                onClick={() => {
                  createShippingLabel();
                  setShowActionSheet(false);
                }}
              >
                In Tem Vận Chuyển
              </Button>

              <Popconfirm
                title="Hoàn tất"
                description="Bạn có chắc chắn muốn hoàn tất đơn hàng này?"
                onConfirm={() => {
                  setShowActionSheet(false);
                  finishOrder();
                }}
                okText="Đồng ý"
                cancelText="Huỷ"
              >
                <Button block>Hoàn tất</Button>
              </Popconfirm>

              <Popconfirm
                title="Huỷ đơn hàng"
                description="Bạn có chắc chắn muốn huỷ đơn hàng này?"
                onConfirm={() => {
                  setShowActionSheet(false);
                  cancelOrder();
                }}
                okText="Đồng ý"
                cancelText="Huỷ"
              >
                <Button danger block>Huỷ</Button>
              </Popconfirm>
            </Space>
          </Drawer>
        </Space>
      </Card>

      <Modal
        title="Chỉnh sửa thông tin hoá đơn"
        open={invoiceEditorOpen}
        onCancel={() => setInvoiceEditorOpen(false)}
        onOk={saveInvoiceInfo}
        okText="Lưu"
        cancelText="Huỷ"
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <div style={{ marginBottom: 6 }}>Tên công ty</div>
            <Input value={invoiceDraft.company_name} onChange={(e) => setInvoiceDraft((prev) => ({ ...prev, company_name: e.target.value }))} />
          </div>
          <div>
            <div style={{ marginBottom: 6 }}>Mã số thuế</div>
            <Input value={invoiceDraft.tax_number} onChange={(e) => setInvoiceDraft((prev) => ({ ...prev, tax_number: e.target.value }))} />
          </div>
          <div>
            <div style={{ marginBottom: 6 }}>Địa chỉ hoá đơn</div>
            <Input value={invoiceDraft.address} onChange={(e) => setInvoiceDraft((prev) => ({ ...prev, address: e.target.value }))} />
          </div>
          <div>
            <div style={{ marginBottom: 6 }}>Email</div>
            <Input value={invoiceDraft.email} onChange={(e) => setInvoiceDraft((prev) => ({ ...prev, email: e.target.value }))} />
          </div>
          <div>
            <div style={{ marginBottom: 6 }}>Ghi chú hoá đơn</div>
            <TextArea rows={3} value={invoiceDraft.note} onChange={(e) => setInvoiceDraft((prev) => ({ ...prev, note: e.target.value }))} />
          </div>
        </Space>
      </Modal>

      <Card bodyStyle={{ padding: 14 }}>
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          {totals.map((item) => (
            <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <Text type="secondary">{item.label}</Text>
              <Text>{formatCurrency(item.value)}</Text>
            </div>
          ))}
          <Divider style={{ margin: '6px 0' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <Text strong>Tổng tiền</Text>
            <Text strong>{formatCurrency(data.total_amount)}</Text>
          </div>
        </Space>
      </Card>

      <Card bodyStyle={{ padding: 14 }}>
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          <Text strong>Sản phẩm</Text>
          {lineItems.map((line) => (
            <Card
              key={`${line.product?.id || ''}-${line.variant?.id || ''}-${line.id || ''}`}
              size="small"
              bodyStyle={{ padding: 10 }}
            >
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <Space size={8} align="start" style={{ width: '100%' }}>
                  {getLineImage(line) && (
                    <Image
                      src={getLineImage(line)}
                      width={42}
                      height={42}
                      style={{ objectFit: 'cover', borderRadius: 6, flexShrink: 0 }}
                      fallback='data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMiIgaGVpZ2h0PSIzMiI+PHJlY3Qgd2lkdGg9IjMyIiBoZWlnaHQ9IjMyIiBmaWxsPSIjZjVmNWY1Ii8+PC9zdmc+'
                    />
                  )}
                  <Space direction="vertical" size={0} style={{ width: '100%' }}>
                    <Text style={{ lineHeight: 1.4 }}>
                      {line.product?.name || '-'}
                    </Text>
                    {line.variant?.variant_title && (
                      <Text type="secondary" style={{ lineHeight: 1.3 }}>
                        {line.variant.variant_title}
                      </Text>
                    )}
                  </Space>
                </Space>

                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <Text type="secondary">Số lượng</Text>
                  <Text>{line.quantity}</Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <Text type="secondary">Đơn giá</Text>
                  <Text>{formatCurrency(line.price)}</Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <Text strong>Thành tiền</Text>
                  <Text strong>{formatCurrency(line.amount)}</Text>
                </div>
              </Space>
            </Card>
          ))}
        </Space>
      </Card>
    </Space>
  );
};

export default OrderDetailMobile;
